import { ButtonClean, ButtonDanger } from 'front-end/src/components'
import {
  CampaignBannerModel,
  CampaignCertificationShareActionModelAsEditor,
  CampaignEmailActionModel,
  CampaignMessageActionModel,
  CampaignPathwayShareActionModel,
  CampaignSurveyShareActionModel,
  CampaignTopicShareActionModel
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import React, { useState } from 'react'

import { t, Trans } from '@lingui/macro'

import { Modal } from '../../../../components/common/modal/Modal'
import { notification } from '../../../../components/common/notifications/Notifications'
import { ICampaignActionToTriggerChange } from '../../campaign-actions/CampaignActions'
import { translateActionType } from '../create-campaign-action-modal/CreateCampaignActionModal'
import {
  useDeleteCampaignBannerMutation,
  useDeleteCampaignEmailMutation,
  useDeleteCampaignMessageMutation,
  useDeleteCertificationShareActionMutation,
  useDeletePathwayShareActionMutation,
  useDeleteSurveyShareActionMutation,
  useDeleteTopicShareActionMutation
} from './deleteCampaignMessage.codegen'
import { getCampaignActionName } from 'front-end/src/pages/manage-campaign-page/common'
import { CampaignActionTypeEnum } from 'shared/enums/campaigns'

export interface IDeleteCampaignActionModalProps {
  show?: boolean
  campaignId: number
  itemToDelete: ICampaignActionToTriggerChange | null
  onHide: () => void
  onHidden?: () => void
  onDelete: () => Promise<void>
}

export default (props: IDeleteCampaignActionModalProps) => {
  const [isDeleting, setIsDeleting] = useState<boolean>(false)

  const isItemOfType = (type: CampaignActionTypeEnum): boolean => {
    return props.itemToDelete?.actionType === type
  }

  const { onHide, onHidden, show } = props
  const type = `${translateActionType(
    props.itemToDelete?.actionType as CampaignActionTypeEnum
  )}`

  if (!props.itemToDelete?.action) {
    return null
  }

  const name = getCampaignActionName(
    props.itemToDelete.action,
    props.itemToDelete?.actionType
  )

  const [deleteCampaignMessage] = useDeleteCampaignMessageMutation()
  const [deleteTopicShareAction] = useDeleteTopicShareActionMutation()
  const [deleteSurveyShareAction] = useDeleteSurveyShareActionMutation()
  const [deletePathwayShareAction] = useDeletePathwayShareActionMutation()
  const [deleteCampaignEmail] = useDeleteCampaignEmailMutation()
  const [deleteCampaignBanner] = useDeleteCampaignBannerMutation()
  const [deleteCertificationShareAction] =
    useDeleteCertificationShareActionMutation()

  const onDelete = async () => {
    setIsDeleting(true)
    if (isItemOfType(CampaignActionTypeEnum.BANNER)) {
      await deleteCampaignBanner({
        variables: {
          id: (props.itemToDelete?.action as CampaignBannerModel).id,
          campaignId: props.campaignId
        }
      })
    }
    if (isItemOfType(CampaignActionTypeEnum.EMAIL)) {
      await deleteCampaignEmail({
        variables: {
          id: (props.itemToDelete?.action as CampaignEmailActionModel).id,
          campaignId: props.campaignId
        }
      })
    }
    if (isItemOfType(CampaignActionTypeEnum.PATHWAY_SHARE_ACTION)) {
      await deletePathwayShareAction({
        variables: {
          id: (props.itemToDelete?.action as CampaignPathwayShareActionModel)
            .id,
          campaignId: props.campaignId
        }
      })
    }
    if (isItemOfType(CampaignActionTypeEnum.CERTIFICATION_SHARE_ACTION)) {
      await deleteCertificationShareAction({
        variables: {
          id: (
            props.itemToDelete
              ?.action as CampaignCertificationShareActionModelAsEditor
          ).id,
          campaignId: props.campaignId
        }
      })
    }
    if (isItemOfType(CampaignActionTypeEnum.SURVEY_SHARE_ACTION)) {
      await deleteSurveyShareAction({
        variables: {
          id: (props.itemToDelete?.action as CampaignSurveyShareActionModel).id,
          campaignId: props.campaignId
        }
      })
    }
    if (isItemOfType(CampaignActionTypeEnum.TOPIC_SHARE_ACTION)) {
      await deleteTopicShareAction({
        variables: {
          id: (props.itemToDelete?.action as CampaignTopicShareActionModel).id,
          campaignId: props.campaignId
        }
      })
    }
    if (isItemOfType(CampaignActionTypeEnum.MESSAGE)) {
      await deleteCampaignMessage({
        variables: {
          id: (props.itemToDelete?.action as CampaignMessageActionModel).id,
          campaignId: props.campaignId
        }
      })
    }
    await props.onDelete()
    notification.info(t`Campaign action was successfully deleted`)
    setIsDeleting(false)
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      onHidden={onHidden}
      header={t`Delete ${type} action`}
      controls={[
        <ButtonClean key={1} onClick={onHide}>
          <Trans>Cancel</Trans>
        </ButtonClean>,
        <ButtonDanger key={2} onClick={onDelete} isLoading={isDeleting}>
          <Trans>Delete</Trans>
        </ButtonDanger>
      ]}
    >
      <Trans>
        Are you sure you want to delete campaign action <b>{name}</b>? This
        cannot be undone.
      </Trans>
    </Modal>
  )
}
